import React from 'react';
import { Box, styled, SxProps, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { ITextWithSpeakerProps } from './types';
import { theme } from '../../../styles/theme';
import { SpeakerIcon } from '../../../../../icons/SpeakerIcon';
import { Text } from '../../Text/Text';
import { StopIcon } from '../../../../../icons/StopIcon';
import { CLASS_SPEAKER_SCROLL } from '../../../constants';

export function TextWithSpeaker ({
	boxSx,
	children,
	textSx,
	text,
	speakOnClick,
	autoSpeak,
	onSpeak,
	onStopSpeak,
}: ITextWithSpeakerProps) {
	const enterDelay = 200;
	const [playMode, setPlayMode] = React.useState(false);
	const data = React.useRef({ playMode });
	data.current = { playMode };

	const boxStyle: SxProps = {
		position: 'relative',

		'& > span': {
			p: '4px 0',
			fontSize: theme.typography.subtitle2,
			lineHeight: theme.spacing(3.25),
		},
		'.context': {
			backgroundColor: theme.palette.info[500],
		},
		'.phrase': {
			bgcolor: theme.palette.info[100],
		},
	};

	const textStyle: SxProps = {
		cursor: 'pointer',
		fontSize: theme.typography.subtitle2.fontSize,
		wordWrap: 'break-word',
	};

	const TooltipStyled = styled(({ className, ...props }: TooltipProps) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))({
		[`& .${tooltipClasses.tooltip}`]: {
			width: theme.spacing(4),
			height: theme.spacing(4),
			marginRight: `${theme.spacing(1)} !important`,
			cursor: 'pointer',
			background: theme.palette.primary.main,
			borderRadius: '50%',
		},
	});

	const onBlur = (e: any) => {
		if (onStopSpeak) {
			const target = e.relatedTarget as HTMLElement;
			if (target && (
				target.classList && target.classList.contains('.text-with-speaker') ||
				target && target.closest('.text-with-speaker')
			)) {
				// not blur
			} else {
				onStopSpeak();
			}
		}
	}

	const handleMouse = (e: React.SyntheticEvent) => {
		if (playMode) {
			onStopSpeak();
			return;
		}

		e.preventDefault();
		if (!playMode) {
			setPlayMode(true);
			onSpeak(() => {
				setPlayMode(false);
			})
		}
	}

	const [tooltipOpen, setTooltipOpen] = React.useState(false);
	const handleTooltipClose = () => {
		if (!playMode) setTooltipOpen(false);
	};
	const handleTooltipOpen = () => {
		setTooltipOpen(true);
	};
	React.useEffect(() => {
		if (!playMode) setTooltipOpen(false);
	}, [playMode]);

	const speechSynthesisText = React.useRef<any>(null);
	React.useEffect(() => {
		setTimeout(() => {
			handleMouseMove();
		}, 500);

		const onSpeechSynthesisEvent = (e: any) => {
			if (speechSynthesisText.current) {
				if (speechSynthesisText.current !== e?.detail?.message || // text switched
					e?.detail?.type !== 'onstart' // speech stopped
				) {
					setPlayMode(false);
					window.removeEventListener('speechSynthesisEvent', onSpeechSynthesisEvent);
				}
			} else {
				speechSynthesisText.current = e?.detail?.message;
			}
		}

		if (autoSpeak) {
			window.addEventListener('speechSynthesisEvent', onSpeechSynthesisEvent);
			setTimeout(() => {
				setPlayMode(true);
				setTooltipOpen(true);
			}, 500);
		}
		return () => {
			if (data.current.playMode) onStopSpeak();
			setPlayMode(false);
			setTooltipOpen(false);
			window.removeEventListener('speechSynthesisEvent', onSpeechSynthesisEvent);
			speechSynthesisText.current = null;
		}
	}, [text]);

	const textHtml = children ? {} : {
		dangerouslySetInnerHTML: { __html: text }
	};

	const ref = React.useRef(null);
	const resizeObserverDebounce = React.useRef<any>(null);
	const scrollEl = React.useRef<any>(null);
	React.useEffect(() => {
		scrollEl.current = ref?.current && ref.current.closest('.' + CLASS_SPEAKER_SCROLL);
		if (scrollEl.current) scrollEl.current.addEventListener('scroll', handleMouseMove);

		const onResize = () => {
			if (resizeObserverDebounce.current) clearTimeout(resizeObserverDebounce.current);
			resizeObserverDebounce.current = setTimeout(handleMouseMove, 100);
		}
		const observer = new ResizeObserver(onResize);
		if (scrollEl.current) observer.observe(scrollEl.current);

		return () => {
			if (scrollEl.current) scrollEl.current.removeEventListener('scroll', handleMouseMove);
		}
	}, []);

	const popperRef = React.useRef(null);
	const positionRef = React.useRef<{ x: number; y: number }>({
		x: 0,
		y: 0,
	});
	const handleMouseMove = () => {
		let box = ref.current?.getBoundingClientRect();
		let scrollElTop = scrollEl.current?.getBoundingClientRect()?.top || 0;
		let boxTop = box?.top || 0;
		let boxBot = box?.bottom || 0;
		let isSticky = boxTop <= scrollElTop;
		let isHidden = false;
		let x = box?.x || 0;
		if (isSticky) {
			boxTop = scrollElTop;
			if (boxBot < scrollElTop + 16) {
				isHidden = true;
				x = -1000;
			}
		}
		let y = boxTop + 12;
		if (!isHidden) {
			let boxHeight = box?.height || 0;
			if (y < 0 || boxHeight < 48) {
				y = boxTop + 12;
			} else {
				let boxBot = box?.bottom || 0;
				if (y > boxBot - 24) {
					if (y < boxTop + 12) {
						y = boxTop + 12;
					} else {
						y = boxBot - 24;
					}
				}
			}
		}
		positionRef.current = {
			x,
			y,
		};

		if (popperRef.current != null) {
			popperRef.current.update();
		}
	};

	const boxClick = speakOnClick
		? { onClick: handleMouse }
		: { onDoubleClick: handleMouse }

	const popperProps = scrollEl.current
		? {
			anchorEl: {
				getBoundingClientRect: () => {
					return new DOMRect(
						positionRef.current.x,
						positionRef.current.y,
						0,
						0,
					);
				},
			},
			popperRef,
		} : {};

	return (
		<Box
			className={'text-with-speaker'}
			sx={{
				...boxStyle,
				...boxSx,
			}}
			tabIndex={0}
			onBlur={onBlur}
			ref={ref}
			{...boxClick}
		>
			<TooltipStyled
				placement={scrollEl.current ? 'left' : 'left-start'}
				open={tooltipOpen}
				onOpen={handleTooltipOpen}
				onClose={handleTooltipClose}
				title={playMode
					? <StopIcon
						sx={{
							width: theme.spacing(1.25),
							height: theme.spacing(1.25),
						}}
					/>
					: <SpeakerIcon
						sx={{
							width: theme.spacing(2.5),
							height: theme.spacing(2.5),
						}}
					/>
				}
				enterDelay={enterDelay}
				enterNextDelay={enterDelay}
				// leaveDelay={500}
				slotProps={{
					popper: {
						onClick: speakOnClick ? ()=>{} : handleMouse,
						...popperProps
					},
				}}
			>
				<Text
					sx={{
						...textStyle,
						...textSx,
					}}
					{...textHtml}
				>
					{children}
				</Text>
			</TooltipStyled>
		</Box>
	);

}
